import React from 'react';
import { Box, Typography } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import { WeatherEventScatter } from './WeatherEventScatter';
import TimeSlider from './TimeSlider';
import type { ChartPoint } from '../services/weatherHistory';
import type { MapView } from '../services/types';

interface EventTimeSeriesProps {
  events: ChartPoint[] | null;
  loading: boolean;
  selectedMap: MapView;
  /**
   * Reports the content's minimum height in px (header + chart at its minimum
   * + slider) whenever it changes, so the hosting window can default to a
   * snug fit.
   */
  onMinHeightChange?: (px: number) => void;
}

// Tall enough for a two-line wrapped category label without rows overlapping.
const CATEGORY_ROW_HEIGHT = 34;
const CHART_AXIS_ALLOWANCE = 64;

export const EventTimeSeries: React.FC<EventTimeSeriesProps> = ({
  events,
  loading,
  selectedMap,
  onMinHeightChange,
}) => {
  // Give every category row a minimum height so none of the Y-axis labels get
  // dropped. When the categories exceed the visible area the stats window
  // scrolls instead of squeezing labels out.
  const categoryCount = React.useMemo(
    () => new Set((events ?? []).map(e => e.yGroup)).size,
    [events]
  );
  const chartMinHeight = categoryCount * CATEGORY_ROW_HEIGHT + CHART_AXIS_ALLOWANCE;

  // Measure the fixed (non-flexing) rows and report the total minimum content
  // height. The chart box itself is measured as its minimum, not its current
  // flexed size, so the report is stable regardless of the window's size.
  const headerRef = React.useRef<HTMLElement>(null);
  const sliderRef = React.useRef<HTMLElement>(null);
  React.useLayoutEffect(() => {
    if (loading || !events || events.length === 0 || !onMinHeightChange) return;
    const headerHeight = headerRef.current?.offsetHeight ?? 0;
    const sliderHeight = sliderRef.current?.offsetHeight ?? 0;
    onMinHeightChange(headerHeight + chartMinHeight + sliderHeight);
  }, [loading, events, chartMinHeight, onMinHeightChange]);

  return (
    <Box
      sx={{
        pointerEvents: 'auto',
        zIndex: 10,
        ml: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        ref={headerRef}
        variant="h6"
        sx={{
          color: 'white',
          textIndent: '10px',
          alignItems: 'center',
          display: 'flex',
          pb: 1,
          flexShrink: 0,
        }}
      >
        <TimelineIcon /> Event Category Time Series
      </Typography>
      {/* The chart absorbs whatever height the date/slider controls below do
          not use, so resizing the stats window resizes the chart instead of
          leaving dead space under the slider. */}
      <Box
        sx={{
          flex: 1,
          minHeight: chartMinHeight,
          position: 'relative',
          width: '100%',
        }}
      >
        <WeatherEventScatter events={events} loading={loading} selectedMap={selectedMap} />
      </Box>

      {/* overflow hidden: the MUI Slider positions its thumb with a centering
          transform, and scroll extents are computed from PRE-transform layout
          boxes, so the thumb's layout box pokes a few px past the block's
          bottom. Without the clip that phantom extent propagates to the stats
          window's scroll container, which then always shows a scrollbar with
          nothing real to scroll. Everything the slider draws (thumb, marks,
          value label) is visually inside this block, so nothing is cut off. */}
      <Box ref={sliderRef} sx={{ flexShrink: 0, overflow: 'hidden' }}>
        <TimeSlider selectedMap={selectedMap} events={events} />
      </Box>
    </Box>
  );
};
